import type { Rng } from "../rng";
import {
	type BaseState,
	type GameDefinition,
	type MoveOutcome,
	otherPlayer,
	type PlayerIndex,
	reject,
} from "../types";

/**
 * A logic grid: four children, four pets, and a set of clues that narrow the
 * answer down to a handful of possibilities. Both players see the same clues
 * and race to work it out, guessing whole grids and being told only how many
 * lines they got right — so the scores coming back are themselves clues.
 *
 * The clues deliberately do *not* pin down a single answer. They used to, and
 * that broke the game: with one grid left, anyone (or any bot) who worked
 * through the card could play a perfect win on move one, and the guess-feedback
 * loop — the actual game — never happened. A small surviving pool means the
 * clues get you close and the guesses take you the rest of the way.
 */

export const GRID_PEOPLE = ["Ana", "Ben", "Cass", "Dev"] as const;
export const GRID_PETS = ["🐶", "🐱", "🐰", "🐢"] as const;
export const GRID_SIZE = GRID_PEOPLE.length;
/** Guesses in total before the puzzle is called on who got closest. */
export const GRID_GUESS_LIMIT = 12;
/**
 * The most grids the clues may leave standing. Much fewer and the card is
 * close to a spoiler; many more and the clues are mostly noise and the game
 * is a blind guess. A pool in the high single digits keeps deduction worth
 * doing from both directions — the clues and the scores.
 */
export const GRID_POOL_MAX = 10;

export interface GridClue {
	/** True for "Ana has the cat", false for "Ana does not have the cat". */
	has: boolean;
	person: number;
	pet: number;
}

export interface LogicGridState extends BaseState {
	kind: "logicgrid";
	clues: GridClue[];
	/** Person `i` owns pet `solution[i]`. Redacted until the game is over. */
	solution: number[];
	/** Every guess so far, with how many lines it got right. */
	guesses: { player: 0 | 1; grid: number[]; correct: number }[];
	/** The best any player has managed, for settling a game nobody solves. */
	best: [number, number];
}

export interface LogicGridMove {
	grid: number[];
}

/** Every way to pair the people with the pets. */
function permutations(size: number): number[][] {
	if (size === 0) return [[]];
	const smaller = permutations(size - 1);
	const all: number[][] = [];
	for (const rest of smaller) {
		for (let slot = 0; slot <= rest.length; slot++) {
			const copy = [...rest];
			copy.splice(slot, 0, size - 1);
			all.push(copy);
		}
	}
	return all;
}

const ALL_GRIDS = permutations(GRID_SIZE);

export function satisfies(grid: readonly number[], clues: readonly GridClue[]): boolean {
	return clues.every((clue) =>
		clue.has ? grid[clue.person] === clue.pet : grid[clue.person] !== clue.pet,
	);
}

/** Grids still possible given the clues — what a solver has to narrow down. */
export function possibleGrids(clues: readonly GridClue[]): number[][] {
	return ALL_GRIDS.filter((grid) => satisfies(grid, clues));
}

/**
 * Builds a puzzle whose clues leave a small pool of possible answers.
 *
 * Clues are added one at a time, each kept only if it rules something out,
 * and the loop stops the moment the pool fits under the ceiling. Then it
 * walks back through them dropping any clue the pool no longer needs, so
 * nothing on the card is spare.
 */
function makePuzzle(rng: Rng): { solution: number[]; clues: GridClue[] } {
	const solution = ALL_GRIDS[rng.int(ALL_GRIDS.length)];
	const clues: GridClue[] = [];

	// Candidate clues are only ever true statements about the solution.
	const candidates: GridClue[] = [];
	for (let person = 0; person < GRID_SIZE; person++) {
		for (let pet = 0; pet < GRID_SIZE; pet++) {
			candidates.push({ has: solution[person] === pet, person, pet });
		}
	}

	// Each round takes the clue that reduces the pool *least* — a pool walked
	// down gently lands near the top of the band rather than crashing through
	// it, which keeps the opening guesses interesting instead of near-certain.
	let pool = ALL_GRIDS.length;
	let remaining = rng.shuffle(candidates);
	while (pool > GRID_POOL_MAX) {
		let best: { clue: GridClue; count: number } | null = null;
		for (const clue of remaining) {
			const count = possibleGrids([...clues, clue]).length;
			if (count >= pool) continue;
			if (!best || count > best.count) best = { clue, count };
		}
		// Nothing reduces any more, or the only reductions would leave a single
		// grid — a spoiler, not a puzzle. Stop with what the card has.
		if (!best || best.count <= 1) break;
		clues.push(best.clue);
		pool = best.count;
		remaining = remaining.filter((clue) => clue !== best.clue);
	}

	// Drop anything the pool no longer needs, so the card stays readable.
	const trimmed = [...clues];
	for (let i = trimmed.length - 1; i >= 0; i--) {
		const without = trimmed.filter((_, index) => index !== i);
		if (possibleGrids(without).length <= GRID_POOL_MAX) trimmed.splice(i, 1);
	}

	return { solution, clues: trimmed };
}

const isGrid = (value: unknown): value is number[] =>
	Array.isArray(value) &&
	value.length === GRID_SIZE &&
	value.every((pet) => Number.isInteger(pet) && pet >= 0 && pet < GRID_SIZE) &&
	new Set(value).size === GRID_SIZE;

export const logicGrid: GameDefinition<LogicGridState, LogicGridMove> = {
	id: "logicgrid",
	category: "education",
	title: "Logic Grid Duel",
	emoji: "🔍",
	blurb: "Work out who owns which pet from the clues, before your friend does.",
	howTo: [
		"The clues tell you who does and doesn't own each pet.",
		"On your turn, give each person a pet and see how many you got right.",
		"First to get all four right wins.",
	],

	create(rng) {
		const { solution, clues } = makePuzzle(rng);
		return {
			kind: "logicgrid",
			clues,
			solution,
			guesses: [],
			best: [0, 0],
			// The first guess buys information for everyone but a point for the
			// guesser, so the opener is drawn by lot rather than handed to seat 0.
			turn: rng.int(2) as PlayerIndex,
			scores: [0, 0],
			result: null,
		};
	},

	parseMove(raw) {
		if (typeof raw !== "object" || raw === null) return null;
		const grid = (raw as { grid?: unknown }).grid;
		return isGrid(grid) ? { grid } : null;
	},

	apply(state, player, move): MoveOutcome<LogicGridState> {
		if (state.result) return reject("This puzzle is already solved.");
		if (state.turn !== player) return reject("It's not your turn yet.");

		const correct = move.grid.filter((pet, person) => state.solution[person] === pet).length;
		const guesses = [...state.guesses, { player, grid: move.grid, correct }];
		const best: [number, number] = [...state.best];
		best[player] = Math.max(best[player], correct);
		const scores: [number, number] = [...best];

		const next: LogicGridState = { ...state, guesses, best, scores };

		if (correct === GRID_SIZE) {
			return { ok: true, state: { ...next, turn: null, result: { winner: player } } };
		}
		// Nobody cracked it. Whoever got closest takes it.
		if (guesses.length >= GRID_GUESS_LIMIT) {
			const winner = best[0] === best[1] ? null : best[0] > best[1] ? 0 : 1;
			return { ok: true, state: { ...next, turn: null, result: { winner } } };
		}
		return { ok: true, state: { ...next, turn: otherPlayer(player) } };
	},

	/**
	 * Every arrangement not yet guessed. This must *not* be the deduced pool:
	 * "grids still standing" shrinks towards the answer as scores come back —
	 * and this list is what the turn clock plays for an idle player and what
	 * the gentler bots pick from. Handing it the deduction meant a missed turn
	 * was expert play performed *for* you. The sharp bot reasons for itself
	 * through `deduceGrid` instead.
	 */
	legalMoves(state, player) {
		if (state.result || state.turn !== player) return [];
		const guessed = new Set(state.guesses.map((guess) => guess.grid.join(",")));
		const fresh = ALL_GRIDS.filter((grid) => !guessed.has(grid.join(",")));
		// The guess limit is well below the number of arrangements, so this
		// can't run dry — but a stale duplicate beats an empty list if it ever did.
		return (fresh.length > 0 ? fresh : ALL_GRIDS).map((grid) => ({ grid }));
	},

	/**
	 * Holds the answer back until the puzzle is over. Worth being honest about
	 * what this does and doesn't buy: the clues plus the public scores narrow
	 * the answer down, so anyone willing to reason in devtools can get ahead
	 * regardless. It stops the answer being *read*, not deduced.
	 */
	redact(state) {
		return state.result ? state : { ...state, solution: [] };
	},

	tick() {
		return null;
	},

	nextTickAt() {
		return null;
	},
};

/**
 * Grids that fit the clues *and* every score reported so far. This is what a
 * person does with a pencil: each guess that comes back "two right" rules out
 * every arrangement that would have scored anything else.
 */
export function stillStanding(state: LogicGridState): number[][] {
	const consistent = possibleGrids(state.clues).filter((grid) =>
		state.guesses.every(
			(guess) => grid.filter((pet, person) => guess.grid[person] === pet).length === guess.correct,
		),
	);
	// Falling back to the clues alone keeps a move available even if the two
	// somehow disagree, which matters because the turn clock draws from here.
	return consistent.length > 0 ? consistent : possibleGrids(state.clues);
}

/** The computer's guess: one of the arrangements it has not ruled out. */
export function deduceGrid(state: LogicGridState, rng: Rng): LogicGridMove | null {
	const pool = stillStanding(state);
	return pool.length === 0 ? null : { grid: pool[rng.int(pool.length)] };
}
